import { Box, HStack, useBreakpointValue } from "@chakra-ui/react"
import { CoverPageOfferFragment as CoverPageOffer } from "@pathwright/graphql-client/src/generated"
import { CarouselGallery } from "@pathwright/ux/src/components/gallery/Gallery"
import { onMeasureType } from "@pathwright/ux/src/components/text/TruncatedHTML"
import { useEffect, useMemo, useRef, useState } from "react"
import { useRegistrationAccesses } from "../registration/access"
import CoverPageDivider from "./CoverPageDivider"
import CoverPageOfferCard, {
  OFFER_BUTTON_HEIGHT,
  OFFER_CONTINUE_CREATE_ANOTHER_HEIGHT,
  OFFER_HEADING_SPACER,
  OFFER_META_HEIGHT,
  OFFER_META_SPACER,
  OFFER_MULTIPLE_OPTIONS_HEIGHT,
  OFFER_PRICE_DESCRIPTION_HEIGHT,
  OFFER_PRICING_SPACER,
  OFFER_TITLE_HEIGHT
} from "./CoverPageOffer"
import { useCoverPageContext } from "./CoverPageProvider"
import { COVER_PAGE_PADDING } from "./constants"
import {
  coverPageContinueShowsCreateAnotherMeta,
  coverPageContinueShowsMeta,
  getCoverPageContinueState
} from "./coverPageContinue"
import { getOfferContinueOptions } from "./coverPageOfferOrdering"
import {
  CoverPageAddAnother,
  CoverPageBlankSlateSection
} from "./design/CoverPageBlankSlate"
import CoverPageSectionTrigger from "./design/CoverPageSectionTrigger"
import {
  offerHasPriceDescription,
  offerShowsBottomMetaRow,
  useCoverPageTranslate
} from "./utils"
import { useSelectedOfferScroll } from "./useSelectedOfferScroll"

const MAX_CARD_HEIGHT = 500
const OFFER_CARD_MAX_WIDTH = 420
// Card heights follow their content; this is a typical card with a few
// feature lines, for the empty Offer Placeholder.
const OFFER_CARD_TYPICAL_ASPECT_RATIO = "388 / 318"
const OFFER_DIVIDER_HEIGHT = 21

/**
 * Collection chrome + truncation cap for Offer cards.
 * Cross-card CTA alignment is handled by flex inside each card; this hook only
 * (1) reserves shared chrome flags and (2) enables TruncatedHTML when any
 * natural card would exceed MAX_CARD_HEIGHT.
 */
function useDynamicOfferHeights(
  offers: Array<CoverPageOffer> | undefined,
  galleryRef: React.RefObject<HTMLDivElement>
) {
  const [heights, setHeights] = useState<
    Record<
      number | string,
      {
        naturalBody: number
        naturalCard: number
      }
    >
  >({})

  const resourceId = useCoverPageContext((v) => v.resourceId)
  const { index: registrationAccessIndex, ready: registrationAccessReady } =
    useRegistrationAccesses()

  const offersShowBottomMetaRow = useMemo(() => {
    if (!offers?.length) return false
    if (offers.some((o) => offerShowsBottomMetaRow(o))) return true
    if (!registrationAccessReady) return false
    return offers.some((o) =>
      coverPageContinueShowsMeta(
        getCoverPageContinueState(
          registrationAccessIndex,
          resourceId,
          getOfferContinueOptions(o)
        )
      )
    )
  }, [offers, registrationAccessIndex, registrationAccessReady, resourceId])

  const offersShowContinueCreateAnother = useMemo(() => {
    if (!offers?.length || !registrationAccessReady) return false
    return offers.some((o) =>
      coverPageContinueShowsCreateAnotherMeta(
        getCoverPageContinueState(
          registrationAccessIndex,
          resourceId,
          getOfferContinueOptions(o)
        )
      )
    )
  }, [offers, registrationAccessIndex, registrationAccessReady, resourceId])

  const offersHavePriceDescription = useMemo(() => {
    return offers?.some((o) => offerHasPriceDescription(o)) ?? false
  }, [offers])

  const offersHaveMultiplePriceOptions = useMemo(() => {
    return offers?.some((o) => (o.price_options?.length || 0) > 1) ?? false
  }, [offers])

  const priceDescriptionHeight = offersHavePriceDescription
    ? OFFER_PRICE_DESCRIPTION_HEIGHT
    : 0
  const pricingSpacer = offersHavePriceDescription ? OFFER_PRICING_SPACER : 0
  const multipleOptionsHeight = offersHaveMultiplePriceOptions
    ? OFFER_MULTIPLE_OPTIONS_HEIGHT
    : 0
  const multipleOptionsSpacer = offersHaveMultiplePriceOptions
    ? OFFER_PRICING_SPACER
    : 0
  const metaHeight = offersShowBottomMetaRow
    ? OFFER_META_HEIGHT +
      (offersShowContinueCreateAnother
        ? OFFER_CONTINUE_CREATE_ANOTHER_HEIGHT
        : 0)
    : 0
  const metaSpacer = offersShowBottomMetaRow ? OFFER_META_SPACER : 0
  const allPadding = 64

  const chromeHeight =
    allPadding +
    OFFER_TITLE_HEIGHT +
    OFFER_HEADING_SPACER +
    OFFER_DIVIDER_HEIGHT +
    multipleOptionsHeight +
    multipleOptionsSpacer +
    OFFER_BUTTON_HEIGHT +
    priceDescriptionHeight +
    pricingSpacer +
    metaHeight +
    metaSpacer

  const truncationBodyBudget = Math.max(0, MAX_CARD_HEIGHT - chromeHeight)

  // Remeasure when offers or reserved chrome change (Continue meta can appear
  // after RegistrationAccess loads).
  useEffect(() => {
    setHeights({})
  }, [offers, chromeHeight])

  useEffect(() => {
    if (!galleryRef.current) return
    const ro = new ResizeObserver(() => {
      setHeights({})
    })
    ro.observe(galleryRef.current)
    return () => ro.disconnect()
  }, [galleryRef])

  const onMeasureBodyFor =
    (id: number | string): onMeasureType =>
    (info) => {
      const naturalBody = info.naturalHeight
      const naturalCard = chromeHeight + naturalBody

      setHeights((prev) => {
        const existing = prev[id]
        if (
          existing &&
          existing.naturalBody === naturalBody &&
          existing.naturalCard === naturalCard
        ) {
          return prev
        }
        return {
          ...prev,
          [id]: { naturalBody, naturalCard }
        }
      })
    }

  const allMeasured = useMemo(() => {
    if (!offers) return false
    return offers.every((o) => heights[o.id] != null)
  }, [offers, heights])

  const tallestNaturalCard = useMemo(() => {
    if (!offers) return 0
    return offers.reduce(
      (max, o) => Math.max(max, heights[o.id]?.naturalCard ?? 0),
      0
    )
  }, [offers, heights])

  // Soft cap always; TruncatedHTML only when measuring or any card exceeds max.
  const anyAboveMax = allMeasured && tallestNaturalCard > MAX_CARD_HEIGHT
  const needsTruncationPass = !allMeasured || anyAboveMax

  return {
    offersShowBottomMetaRow,
    offersShowContinueCreateAnother,
    offersHavePriceDescription,
    offersHaveMultiplePriceOptions,
    maxCardHeight: MAX_CARD_HEIGHT,
    bodyMaxHeightForTruncation: needsTruncationPass
      ? truncationBodyBudget
      : undefined,
    onMeasureBodyFor
  }
}

const CoverPageOffers = () => {
  const { t } = useCoverPageTranslate()
  const offersSectionRef = useCoverPageContext((v) => v.offersSectionRef)
  const offers = useCoverPageContext((v) => v.offers)
  const immersiveActive = useCoverPageContext((v) => v.immersiveActive)
  const isPanelOpen = useCoverPageContext((v) => v.isDesignMode)
  const visibleItems = useBreakpointValue({
    base: 1,
    md: isPanelOpen ? 1 : 2,
    lg: isPanelOpen ? 1 : 2,
    xl: isPanelOpen ? 2 : 2
  })
  const galleryRef = useRef<HTMLDivElement>(null)
  const resourceId = useCoverPageContext((v) => v.resourceId)
  const offerId = useCoverPageContext((v) => v.offerId)
  const resourceIsReady = useCoverPageContext((v) => v.resourceIsReady)
  const scrollContainerRef = useCoverPageContext((v) => v.scrollContainerRef)
  // Offers can arrive before the overview and preview. Their initial position
  // is not the final scroll target until the full resource has loaded.
  useSelectedOfferScroll({
    galleryRef,
    scrollContainerRef,
    resourceId,
    offerId: immersiveActive ? null : offerId,
    layoutIsReady:
      resourceIsReady && !!offers?.some((offer) => offer.id === offerId)
  })
  const isMobile = useCoverPageContext((v) => v.isMobile)

  const {
    offersShowBottomMetaRow,
    offersShowContinueCreateAnother,
    offersHavePriceDescription,
    offersHaveMultiplePriceOptions,
    maxCardHeight,
    bodyMaxHeightForTruncation,
    onMeasureBodyFor
  } = useDynamicOfferHeights(offers, galleryRef)

  if (!offers?.length)
    return (
      // Shaped like an Offer card: same max width and radius.
      <CoverPageBlankSlateSection
        kind="offer"
        title={t("join")}
        sectionRef={offersSectionRef}
        maxW={`${OFFER_CARD_MAX_WIDTH - 32}px`}
        aspectRatio={OFFER_CARD_TYPICAL_ASPECT_RATIO}
        align="center"
      />
    )

  return (
    <>
      {!immersiveActive && (
        <CoverPageDivider title={t("join")} titleMargin="1rem" />
      )}
      <Box position="relative" w="100%" role="group" id="offers">
        <CoverPageSectionTrigger sectionKey="offers" top={"-40px"} />
        <HStack
          ref={offersSectionRef}
          w="100%"
          scrollMarginTop="150px"
          css={{
            "& .carousel-item": {
              maxW: `${OFFER_CARD_MAX_WIDTH}px`,
              overflowY: "visible",
              position: "relative",
              borderRadius: "var(--chakra-radii-card)"
            },
            "& .carousel-item-content": {
              borderRadius: "var(--chakra-radii-card)",
              overflow: "visible"
            }
          }}
        >
          <Box ref={galleryRef} w="100%">
            <CarouselGallery
              items={offers}
              renderItem={(offer) => (
                <CoverPageOfferCard
                  key={offer.id}
                  offer={offer}
                  displayMeta={offersShowBottomMetaRow}
                  displayDescription={offersHavePriceDescription}
                  reserveMultipleOptionsHeight={offersHaveMultiplePriceOptions}
                  reserveContinueCreateAnother={offersShowContinueCreateAnother}
                  maxCardHeight={maxCardHeight}
                  maxBodyHeightForTruncation={bodyMaxHeightForTruncation}
                  onMeasureBody={onMeasureBodyFor(offer.id)}
                />
              )}
              // To the right of the last card, where another would join it.
              trailing={<CoverPageAddAnother kind="offer" />}
              scrollBehavior={isMobile ? "smooth" : "snap"}
              itemsPerRow={visibleItems}
              maxItemHeight={`${maxCardHeight}px`}
              gap={0}
              spacingY="0"
              spacingX={`calc(${COVER_PAGE_PADDING.md} - 1rem)`}
              enableStagger
            />
          </Box>
        </HStack>
      </Box>
    </>
  )
}

export default CoverPageOffers
